/**
 * Fichas de evidencia.
 *
 * Tres tipos con reglas distintas sobre el mismo endpoint: documental,
 * estadística y de opinión. La nota de una ficha cuantitativa la recalcula el
 * backend en cada cambio, así que tras editar periodos hay que releer la ficha
 * en vez de recalcular en cliente.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type { Ficha } from '@/interfaces/Ficha'
import { crearFichaService, getFichaByIdService, getFichasService, eliminarFichaService, actualizarFichaService, uploadArchivoFichaService} from '@/services/FichasService'

/** Store `GestionFichas`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionFichas = defineStore('GestionFichas', {
  state: () => ({
    fichas: [] as Ficha[],
    fichaActual: null as Ficha | null,
    urlFicha: `${process.env.VUE_APP_API_BASE_URL}/api/fichas`
  }),

  actions: {
    async getFichaById(id: number) {
      const data = await getFichaByIdService(this.urlFicha, id)
      if (data) this.fichaActual = data
      return data
    },

    async crearFicha(payload: Omit<Ficha, 'id_ficha'>): Promise<{ ok: boolean; ficha?: Ficha; error?: string }> {
      const result = await crearFichaService(this.urlFicha, payload)
      if (result.ok && result.data) {
        this.fichas.push(result.data)
        this.fichaActual = result.data
        return { ok: true, ficha: result.data }
      }
      return { ok: false, error: result.error }
    },

    async actualizarFicha(id: number, payload: Partial<Ficha>) {
      const actualizada = await actualizarFichaService(this.urlFicha, id, payload)
      if (actualizada) {
        const index = this.fichas.findIndex(f => f.id_ficha === id)
        if (index !== -1) {
          this.fichas[index] = actualizada
        }
        this.fichaActual = actualizada
      }
      return actualizada
    },

    async eliminarFicha(id: number) {
      const eliminada = await eliminarFichaService(this.urlFicha, id)
      if (eliminada) {
        this.fichas = this.fichas.filter(f => f.id_ficha !== id)
        if (this.fichaActual?.id_ficha === id) {
          this.fichaActual = null
        }
      }
      return eliminada
    },

    async listarFichas(params?: { id_instrumento?: number; tipo?: string; estado?: string }) {
      const data = await getFichasService(this.urlFicha, params)
      if (data) this.fichas = data
      return data
    },

    async uploadArchivo(idFicha: number, archivo: File) {
      const result = await uploadArchivoFichaService(this.urlFicha, idFicha, archivo)
      if (result.ok && result.documento) {
        const idx = this.fichas.findIndex(f => f.id_ficha === idFicha)
        if (idx !== -1) {
          this.fichas[idx] = { ...this.fichas[idx], documento: result.documento }
        }
        if (this.fichaActual?.id_ficha === idFicha) {
          this.fichaActual = { ...this.fichaActual, documento: result.documento }
        }
      }
      return result
    }
  }
})